export const STOCK_COUNT_ADJUSTMENT_TYPE = Object.freeze({
  POSITIVE: "POSITIVE",
  NEGATIVE: "NEGATIVE",
  ZERO: "ZERO",
});

export const STOCK_COUNT_ITEM_TYPE = Object.freeze({
  PURCHASE_ITEM: "PURCHASE_ITEM",
  PREPARED_PRODUCT: "PREPARED_PRODUCT",
});

export const STOCK_COUNT_ITEM_TYPE_LABELS = Object.freeze({
  [STOCK_COUNT_ITEM_TYPE.PURCHASE_ITEM]: "Ítems",
  [STOCK_COUNT_ITEM_TYPE.PREPARED_PRODUCT]: "Elaborados",
});

export const STOCK_COUNT_SECTOR = Object.freeze({
  KITCHEN: "KITCHEN",
  BAR: "BAR",
});

export const STOCK_COUNT_SECTOR_LABELS = Object.freeze({
  [STOCK_COUNT_SECTOR.KITCHEN]: "Cocina",
  [STOCK_COUNT_SECTOR.BAR]: "Barra",
});

export const STOCK_COUNT_ADJUSTMENT_TYPE_LABELS = Object.freeze({
  [STOCK_COUNT_ADJUSTMENT_TYPE.POSITIVE]: "Positivo",
  [STOCK_COUNT_ADJUSTMENT_TYPE.NEGATIVE]: "Negativo",
  [STOCK_COUNT_ADJUSTMENT_TYPE.ZERO]: "Neutro",
});

export const STOCK_COUNT_ADJUSTMENT_INDICATOR_COLORS = Object.freeze({
  [STOCK_COUNT_ADJUSTMENT_TYPE.NEGATIVE]: "rgba(220, 92, 92, 0.55)",
  [STOCK_COUNT_ADJUSTMENT_TYPE.POSITIVE]: "rgba(196, 132, 72, 0.62)",
  [STOCK_COUNT_ADJUSTMENT_TYPE.ZERO]: "rgba(72, 180, 120, 0.55)",
  DEFAULT: "rgba(180, 180, 190, 0.35)",
});

function asNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function asNullableNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function asNullableDecimal(value) {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
}

export function getStockCountItemDisplayName(itemSnapshot = {}) {
  if (itemSnapshot.itemName) return String(itemSnapshot.itemName);

  if (itemSnapshot.preparedProductName) {
    return String(itemSnapshot.preparedProductName);
  }

  const parts = [
    itemSnapshot.ingredientName,
    itemSnapshot.brand,
    itemSnapshot.presentation,
  ].filter(Boolean);

  if (parts.length) return parts.join(" · ");
  return "—";
}

export function normalizeStockCountItem(item = {}) {
  const itemType = Object.values(STOCK_COUNT_ITEM_TYPE).includes(item.itemType)
    ? item.itemType
    : STOCK_COUNT_ITEM_TYPE.PURCHASE_ITEM;

  return {
    ...item,
    id: asNumber(item.id),
    stockCountId: asNumber(item.stockCountId),
    itemType,
    ingredientPurchaseItemId: asNullableNumber(
      item.ingredientPurchaseItemId
    ),
    preparedProductId: asNullableNumber(item.preparedProductId),
    categoryIdSnapshot: asNullableNumber(item.categoryIdSnapshot),
    supplierIdSnapshot: asNullableNumber(item.supplierIdSnapshot),
    sectorSnapshot: Array.isArray(item.sectorSnapshot)
      ? item.sectorSnapshot.filter((sector) =>
          Object.values(STOCK_COUNT_SECTOR).includes(sector)
        )
      : null,
    itemSnapshot: item.itemSnapshot || {},
    countUnitSnapshot: String(item.countUnitSnapshot || ""),
    baseUnitSnapshot: String(item.baseUnitSnapshot || ""),
    conversionFactorSnapshot: asNullableDecimal(item.conversionFactorSnapshot),
    theoreticalBaseQuantity: asNullableDecimal(item.theoreticalBaseQuantity),
    counterQuantity: asNullableDecimal(item.counterQuantity),
    auditorQuantity: asNullableDecimal(item.auditorQuantity),
    adjustmentOverrideQuantity: asNullableDecimal(item.adjustmentOverrideQuantity),
    counterBaseQuantity: asNullableDecimal(item.counterBaseQuantity),
    differenceQuantity: asNullableDecimal(item.differenceQuantity),
    differenceBaseQuantity: asNullableDecimal(item.differenceBaseQuantity),
    requiresAudit: Boolean(item.requiresAudit),
    visibleTheoreticalQuantity: asNullableDecimal(item.visibleTheoreticalQuantity),
    suggestedAdjustmentQuantity: asNullableDecimal(item.suggestedAdjustmentQuantity),
    manualAdjustmentQuantity: asNullableDecimal(item.manualAdjustmentQuantity),
    finalAdjustmentQuantity: asNullableDecimal(item.finalAdjustmentQuantity),
    currentStockQuantity: asNullableDecimal(item.currentStockQuantity),
    hasStockConflict: Boolean(item.hasStockConflict),
    adjustmentType: item.adjustmentType || null,
    adjustmentEditedByUserId:
      item.adjustmentEditedByUserId === null ||
      item.adjustmentEditedByUserId === undefined
        ? null
        : asNumber(item.adjustmentEditedByUserId),
    adjustmentEditedAt: item.adjustmentEditedAt || null,
    createdAt: item.createdAt || null,
    updatedAt: item.updatedAt || null,
  };
}

export function normalizeStockCountItemList(response = {}) {
  return {
    data: Array.isArray(response.data)
      ? response.data.map(normalizeStockCountItem)
      : [],
    meta: {
      page: asNumber(response.meta?.page, 1),
      limit: asNumber(response.meta?.limit, 50),
      totalItems: asNumber(response.meta?.totalItems),
      totalPages: asNumber(response.meta?.totalPages),
      hasNextPage: Boolean(response.meta?.hasNextPage),
      hasPreviousPage: Boolean(response.meta?.hasPreviousPage),
      hasStockConflicts: Boolean(response.meta?.hasStockConflicts),
      groups: Array.isArray(response.meta?.groups)
        ? response.meta.groups
            .filter((group) =>
              Object.values(STOCK_COUNT_ITEM_TYPE).includes(group?.itemType)
            )
            .map((group) => ({
              itemType: group.itemType,
              itemCount: asNumber(group.itemCount),
            }))
        : [],
    },
  };
}

export function normalizeStockCountCategories(response = {}) {
  const payload = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
      ? response
      : [];

  return payload
    .filter((category) => category?.id && category?.name)
    .map((category) => ({
      id: asNumber(category.id),
      name: String(category.name),
    }))
    .sort((left, right) =>
      left.name.localeCompare(right.name, "es", { sensitivity: "base" })
    );
}

const ADJUSTMENT_INPUT_PATTERN = /^-?(0|[1-9]\d*)(\.\d{1,2})?$/;

export function validateAdjustmentInput(value) {
  const normalized = String(value ?? "").trim().replace(",", ".");
  if (!normalized) return "Ingresá una cantidad de ajuste.";
  if (!ADJUSTMENT_INPUT_PATTERN.test(normalized)) {
    return "El ajuste debe ser un número con hasta 2 decimales.";
  }
  return "";
}

export function parseAdjustmentInput(value) {
  const normalized = String(value ?? "").trim().replace(",", ".");
  if (!normalized) return null;
  return normalized;
}
